


import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { API_ENDPOINTS } from '../../../utils/GlobalAPI';
import Pagination from '../Pagination';
import AdminPageHeader from '../AdminPageHeader';
import { FaEnvelopeOpenText, FaSearch, FaFilter, FaTrash } from 'react-icons/fa';

const statusOptions = ['pending', 'converted', 'not converted', 'contact again'];

const AdminInquiry = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({ total: 0, thisMonth: 0 });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Delete an inquiry
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_ENDPOINTS.CONTACT}/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to delete inquiry');
      }
      setInquiries(inquiries => inquiries.filter(q => q._id !== id));
    } catch (err) {
      setError(err.message || 'Error deleting inquiry');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all inquiries
  useEffect(() => {
    const fetchInquiries = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(API_ENDPOINTS.CONTACT);
        if (!res.ok) throw new Error('Failed to fetch inquiries');
        const data = await res.json();
        setInquiries(data);
        // Stats
        setStats({
          total: data.length,
          thisMonth: data.filter(q => {
            const d = new Date(q.createdAt);
            const now = new Date();
            return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
          }).length
        });
      } catch (err) {
        setError(err.message || 'Error fetching inquiries');
      } finally {
        setLoading(false);
      }
    };
    fetchInquiries();
  }, []);

  // Change status of an inquiry
  const handleStatusChange = async (id, newStatus) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_ENDPOINTS.CONTACT}/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to update status');
      }
      const data = await res.json();
      setInquiries(inquiries => inquiries.map(q => q._id === id ? { ...q, status: data.contact.status } : q));
    } catch (err) {
      setError(err.message || 'Error updating status');
    } finally {
      setLoading(false);
    }
  };

  // Filtered and searched inquiries
  const filtered = inquiries.filter(q =>
    (statusFilter === 'all' || q.status === statusFilter) &&
    (
      q.name.toLowerCase().includes(search.toLowerCase()) ||
      q.email.toLowerCase().includes(search.toLowerCase()) ||
      q.phone.toLowerCase().includes(search.toLowerCase()) ||
      q.service.toLowerCase().includes(search.toLowerCase())
    )
  );

  // Pagination logic
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedInquiries = filtered.slice(startIndex, endIndex);

  const handlePageChange = (page, newItemsPerPage = itemsPerPage) => {
    setCurrentPage(page);
    if (newItemsPerPage !== itemsPerPage) {
      setItemsPerPage(newItemsPerPage);
      setCurrentPage(1);
    }
  };

  return (
    <div className="bg-slate-50/50 min-h-screen">
      <AdminPageHeader
        title="Contact Inquiry Log"
        subtitle="Manage and track customer consultation and services requests"
        icon={<FaEnvelopeOpenText className="w-6 h-6" />}
      />

      <div className="pt-32 p-6 max-w-7xl mx-auto space-y-6">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-rose-50 border border-rose-200 rounded-xl p-4"
          >
            <div className="text-rose-600 font-semibold">{error}</div>
          </motion.div>
        )}

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {[
            { label: 'Total Inquiries', val: stats.total, color: 'text-slate-800', bg: 'bg-white border-slate-100' },
            { label: 'Received This Month', val: stats.thisMonth, color: 'text-purple-600', bg: 'bg-white border-purple-100' },
            { label: 'Pending Review', val: inquiries.filter(q => q.status === 'pending').length, color: 'text-amber-600', bg: 'bg-white border-amber-100' },
            { label: 'Converted Leads', val: inquiries.filter(q => q.status === 'converted').length, color: 'text-emerald-600', bg: 'bg-white border-emerald-100' }
          ].map((stat, i) => (
            <div key={i} className={`border rounded-2xl p-5 shadow-sm hover:shadow transition-shadow ${stat.bg}`}>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{stat.label}</div>
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.val}</div>
            </div>
          ))}
        </motion.div>

        {/* Unified Search Control Bar */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05 }}
          className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col md:flex-row gap-4 items-center justify-between"
        >
          {/* Search Input */}
          <div className="relative w-full md:max-w-md">
            <FaSearch className="absolute left-3 top-3.5 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by client name, email, phone or service..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl text-slate-800 text-sm focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition placeholder:text-slate-400"
            />
          </div>

          {/* Filter Status */}
          <div className="flex items-center space-x-3 w-full md:w-auto">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 whitespace-nowrap">
              <FaFilter className="w-3.5 h-3.5 text-slate-400" />
              Filter Queue:
            </span>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="w-full md:w-44 border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition bg-white"
            >
              <option value="all">All Statuses</option>
              {statusOptions.map(opt => (
                <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
              ))}
            </select>
          </div>
        </motion.div>

        {/* Data Table */}
        {loading && inquiries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white border border-slate-100 rounded-2xl shadow-sm">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mb-4"></div>
            <p className="text-sm text-slate-500">Retrieving inquiry logs...</p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="space-y-4"
          >
            <div className="overflow-x-auto bg-white rounded-2xl border border-slate-100 shadow-sm">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Client Details</th>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Requested Service</th>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Status Check</th>
                    <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Submitted Timestamp</th>
                    <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {paginatedInquiries.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-16 text-slate-500">
                        <div className="flex flex-col items-center">
                          <div className="w-14 h-14 bg-slate-50 rounded-full flex items-center justify-center mb-3 border border-slate-100">
                            <FaEnvelopeOpenText className="w-6 h-6 text-slate-400" />
                          </div>
                          <p className="text-base font-semibold text-slate-700">No inquiries found</p>
                          <p className="text-xs text-slate-400 mt-1">Try adjusting your filters or search keywords</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedInquiries.map((q) => (
                      <tr key={q._id} className="hover:bg-slate-50/40 transition-colors">
                        {/* Client details */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="font-semibold text-slate-800 text-sm">{q.name}</div>
                            <div className="text-[11px] text-slate-400 mt-0.5">{q.email}</div>
                            {q.phone && (
                              <div className="text-[10px] text-slate-400 font-mono mt-0.5">{q.phone}</div>
                            )}
                          </div>
                        </td>

                        {/* Requested Service */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700 font-medium">
                          <span className="px-2.5 py-1 bg-slate-50 border border-slate-200/50 rounded-lg text-xs font-semibold text-slate-700">
                            {q.service}
                          </span>
                        </td>

                        {/* Status Check */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={q.status}
                            onChange={e => handleStatusChange(q._id, e.target.value)}
                            className={`border rounded-xl px-2.5 py-1.5 text-xs font-semibold focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition bg-white shadow-sm ${
                              q.status === 'pending'
                                ? 'text-amber-700 border-amber-200 bg-amber-50/20'
                                : q.status === 'converted'
                                ? 'text-emerald-700 border-emerald-200 bg-emerald-50/20'
                                : q.status === 'not converted'
                                ? 'text-rose-700 border-rose-200 bg-rose-50/20'
                                : 'text-purple-700 border-purple-200 bg-purple-50/20'
                            }`}
                          >
                            {statusOptions.map(opt => (
                              <option key={opt} value={opt} className="text-slate-700 font-medium">
                                {opt.charAt(0).toUpperCase() + opt.slice(1)}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* Submitted Date */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 font-medium">
                          {q.createdAt ? new Date(q.createdAt).toLocaleString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : '-'}
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={() => handleDelete(q._id)}
                            disabled={loading}
                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg border border-transparent hover:border-rose-100 transition-all disabled:opacity-50"
                            title="Delete Inquiry Log"
                          >
                            <FaTrash className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
              totalItems={filtered.length}
              showItemsPerPage={true}
              itemsPerPageOptions={[5, 10, 20, 50]}
            />
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminInquiry;

